import { useRef } from 'react';

import { InsightsGetValuesOptions } from 'contextProviders/InsightsContentProvider';
import { InsightsGadgetValues } from 'contextProviders/InsightsContentProvider/viewTypes';

import buildGadgetValuesDigest from './buildGadgetValuesDigest';
import useRegisterValues from './useRegisterValues';

// Makes a chart-shaped gadget's rendered figures available to the AI Insights
// Chat, so it can answer "why is X low?" about the number the user is looking at.
//
// Called from useV5ChartData, which every chart-shaped gadget's data hook funnels
// through — bar, pie, timeSeries, smartCard, card, waterfall, radar, scatter,
// bubble, packedBubble, treeMap, funnel. Table- and stack-shaped gadgets resolve
// through useMetricMatrix instead; see useRegisterInsightsMatrixValues.
const useRegisterInsightsGadgetValues = ({
  chartDef,
  data,
  isLoading,
}: {
  chartDef: V5ChartDefinition;
  data?: V5ChartData;
  isLoading: boolean;
}): void => {
  // When the current numbers arrived, so the digest can say "as displayed at …"
  // rather than implying they're live at question time.
  const receivedAtRef = useRef<string>('');
  const previousDataRef = useRef<V5ChartData | undefined>(undefined);
  if (data !== previousDataRef.current) {
    previousDataRef.current = data;
    if (data) {
      receivedAtRef.current = new Date().toISOString();
    }
  }

  const latestRef = useRef({ chartDef, data, isLoading });
  latestRef.current = { chartDef, data, isLoading };

  useRegisterValues({
    source: 'v5',
    getValues: (opts?: InsightsGetValuesOptions): InsightsGadgetValues => {
      const current = latestRef.current;
      // Mid-refetch: reporting the previous filters' numbers would be worse than
      // reporting none.
      if (current.isLoading || !current.data) {
        return { isLoading: true };
      }
      return buildGadgetValuesDigest({
        chartDef: current.chartDef,
        data: current.data,
        asOf: receivedAtRef.current,
        pinnedLabels: opts?.pinnedLabels,
      });
    },
  });
};

export default useRegisterInsightsGadgetValues;
